import Input from '../ui/Input';
import Select from '../ui/Select';

/**
 * The dynamic-field builder row — shared by the workflow "Edit step" modal and the
 * Nationalities master modal. Renders one field definition with its type, options and
 * validation controls. Pairs with <DynamicField /> which renders the field for answering.
 */

export const FIELD_TYPES = [
  { value: 'text', label: 'Text' },
  { value: 'number', label: 'Number' },
  { value: 'checkbox', label: 'Check box' },
  { value: 'radio', label: 'Radio button' },
  { value: 'select', label: 'Select field' },
  { value: 'multiselect', label: 'Multi select field' },
  { value: 'date', label: 'Date' },
];

export const OPTION_TYPES = ['checkbox', 'radio', 'select', 'multiselect'];

export const DATE_RULE_OPERATORS = [
  { value: 'before', label: 'Less than' },
  { value: 'after', label: 'Greater than' },
];

export const DATE_RULE_REFERENCES = [
  { value: 'today', label: 'Today' },
  { value: '1_week', label: 'One week from today' },
  { value: '1_month', label: 'One month from today' },
  { value: '1_year', label: 'One year from today' },
];

export function newId() {
  return typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : `f_${Date.now()}_${Math.random()}`;
}

export function emptyField() {
  return { id: newId(), label: '', type: 'text', options: [], validation: { required: false, date_rule: null } };
}

export default function FieldEditor({ field, onChange, onRemove }) {
  const hasOptions = OPTION_TYPES.includes(field.type);
  const isDate = field.type === 'date';
  const hasDateRule = isDate && !!field.validation.date_rule;

  const setOption = (idx, value) => {
    const options = [...field.options];
    options[idx] = value;
    onChange({ ...field, options });
  };

  const addOption = () => onChange({ ...field, options: [...field.options, ''] });
  const removeOption = (idx) => onChange({ ...field, options: field.options.filter((_, i) => i !== idx) });

  const setRequired = (required) => onChange({ ...field, validation: { ...field.validation, required } });

  const toggleDateRule = (enabled) =>
    onChange({
      ...field,
      validation: { ...field.validation, date_rule: enabled ? { operator: 'after', reference: 'today' } : null },
    });

  const setDateRule = (patch) =>
    onChange({ ...field, validation: { ...field.validation, date_rule: { ...field.validation.date_rule, ...patch } } });

  return (
    <div className="rounded-md border border-gray-200 p-3">
      <div className="flex items-start gap-2">
        <Input
          className="flex-1"
          placeholder="Field label"
          value={field.label}
          onChange={(e) => onChange({ ...field, label: e.target.value })}
        />
        <Select
          className="w-44 shrink-0"
          value={field.type}
          onChange={(e) => {
            const type = e.target.value;
            onChange({
              ...field,
              type,
              options: OPTION_TYPES.includes(type) ? (field.options.length ? field.options : ['', '']) : [],
              validation: { required: field.validation.required, date_rule: type === 'date' ? field.validation.date_rule : null },
            });
          }}
        >
          {FIELD_TYPES.map((t) => (
            <option key={t.value} value={t.value}>
              {t.label}
            </option>
          ))}
        </Select>
        <button type="button" onClick={onRemove} className="mt-2 shrink-0 text-xs text-red-500 hover:underline">
          Remove
        </button>
      </div>

      {hasOptions && (
        <div className="mt-2 space-y-1.5 pl-1">
          <p className="text-xs font-medium text-gray-500">Answer options</p>
          {field.options.map((opt, idx) => (
            <div key={idx} className="flex items-center gap-2">
              <Input className="flex-1" placeholder={`Option ${idx + 1}`} value={opt} onChange={(e) => setOption(idx, e.target.value)} />
              {field.options.length > 2 && (
                <button type="button" onClick={() => removeOption(idx)} className="text-xs text-gray-400 hover:text-red-600">
                  ✕
                </button>
              )}
            </div>
          ))}
          <button type="button" onClick={addOption} className="text-xs text-primary hover:underline">
            + Add option
          </button>
        </div>
      )}

      <div className="mt-2.5 space-y-1.5 border-t border-gray-100 pt-2 pl-1">
        <p className="text-xs font-medium text-gray-500">Validation</p>
        <label className="flex items-center gap-2 text-sm text-gray-700">
          <input type="checkbox" className="accent-primary" checked={field.validation.required} onChange={(e) => setRequired(e.target.checked)} />
          Required
        </label>

        {isDate && (
          <div className="space-y-1.5">
            <label className="flex items-center gap-2 text-sm text-gray-700">
              <input type="checkbox" className="accent-primary" checked={hasDateRule} onChange={(e) => toggleDateRule(e.target.checked)} />
              Date must be relative to today
            </label>
            {hasDateRule && (
              <div className="flex items-center gap-2 pl-6">
                <Select className="w-36" value={field.validation.date_rule.operator} onChange={(e) => setDateRule({ operator: e.target.value })}>
                  {DATE_RULE_OPERATORS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </Select>
                <Select className="w-48" value={field.validation.date_rule.reference} onChange={(e) => setDateRule({ reference: e.target.value })}>
                  {DATE_RULE_REFERENCES.map((r) => (
                    <option key={r.value} value={r.value}>
                      {r.label}
                    </option>
                  ))}
                </Select>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
